<template>
    <div :class="buttonAlignmentClass">
        <NuxtLink
            :to="
                url &&
                (url.startsWith('/') ||
                    url.startsWith('http') ||
                    url.startsWith('mailto:') ||
                    url.startsWith('tel:') ||
                    url.startsWith('#'))
                    ? url
                    : '/' + url
            "
            :target="target"
            :class="['btn flex w-max items-center justify-center lg:w-max', classes, buttonStyles]">
            <slot></slot>
            {{ title }}
        </NuxtLink>
    </div>
</template>

<script setup lang="ts">
    import { stegaClean } from '@sanity/client/stega';

    const { getUrlById } = useCategorySeoUrl();

    const isLoaded = ref(false);

    onMounted(() => (isLoaded.value = true));

    interface ButtonProps {
        _type?: string;
        internalLink?: string | object;
        externalUrl?: string;
        buttonType?: string;
        buttonTypeMobile?: string;
        classes?: string;
        type?: string;
        text?: string;
        title?: string;
        link?: string;
        Theme?: string;
        target?: string;
        buttonAlignment?: string;
    }

    const props = withDefaults(defineProps<ButtonProps>(), {
        link: '#',
        target: '_self',
        buttonAlignment: 'center',
    });

    const buttonStyles = reactive({
        'btn-primary':
            stegaClean(props.buttonTypeMobile) === 'primary' ||
            (!stegaClean(props.buttonTypeMobile) && props.type === 'primary'),
        'btn-secondary':
            stegaClean(props.buttonTypeMobile) === 'secondary' ||
            (!stegaClean(props.buttonTypeMobile) && props.type === 'secondary'),
        'btn-underline':
            stegaClean(props.buttonTypeMobile) === 'underline' ||
            (!stegaClean(props.buttonTypeMobile) && props.type === 'underline'),
        'lg:btn-primary': props.type === 'primary',
        'lg:btn-secondary': props.type === 'secondary',
        'lg:btn-underline': props.type === 'underline',
    });

    const buttonAlignmentClass = computed(() => {
        switch (props.buttonAlignment) {
            case 'left':
                return 'justify-start';
            case 'right':
                return 'justify-end';
            case 'center':
            default:
                return 'justify-center';
        }
    });

    const attr = ref(props);
    const url = ref(props.link);
    const target = ref(props.target);
    const button = ref(props.buttonType);

    watch(
        attr,
        async () => {
            url.value = props.link;
            target.value = props.target;
            button.value = props.buttonType;

            if (button.value === 'external' && props.externalUrl) {
                target.value = '_blank';
                url.value = props.externalUrl;
            } else if (button.value === 'internal') {
                // Only resolve URL if no link prop is provided
                try {
                    const linkRef =
                        typeof props.internalLink === 'object' && props.internalLink && '_ref' in props.internalLink
                            ? props.internalLink._ref
                            : props.internalLink;
                    const seoUrl = getUrlById(linkRef);
                    if (seoUrl) {
                        url.value = seoUrl.toLowerCase();
                    }
                } catch (error) {
                    console.warn('Failed to resolve SEO URL:', error);
                }
            }
        },
        { immediate: true, once: true },
    );
</script>

<style scoped>
    @reference "tailwindcss";
    .text-white .btn-underline {
        @apply border-white;
    }
</style>
